import React from "react";
import { Tab, Tabs } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import FileUploadTab from "./tabs/fileUploadTab/FileUploadTab";
import FileOverviewTab from "./tabs/fileOverviewTab/FileOverviewTab";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import APIErrorHelper from "../../../../helpers/APIErrorHelper";
import equals from "fast-deep-equal";
import BaseDialog from "../../../../components/dialogs/baseDialog/BaseDialog";

class UploadManagerDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      studentFileProposals: [],
      studentFileProposalsIndexMap: new Map(),
      dialogApiError: ""
    };

    this.fetchData = this.fetchData.bind(this);
    this.onHide = this.onHide.bind(this);
    this.onUpload = this.onUpload.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.registerEventListener = this.registerEventListener.bind(this);
  }

  componentDidMount() {
    this.registerEventListener();
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (!equals(prevProps.studentExamination, this.props.studentExamination)) {
      this.fetchData();
    }
  }

  async fetchData() {
    try {
      const studentFileProposals = await CamplaCoreService.getAllStudentFilesByStudentExamination(
        this.props.studentExamination.examination._id
      );
      studentFileProposals.forEach((sf, idx) =>
        this.state.studentFileProposalsIndexMap.set(
          sf._id,
          this.state.studentFileProposals.length + idx
        )
      );
      this.setState({
        studentFileProposalsIndexMap: this.state.studentFileProposalsIndexMap,
        studentFileProposals: studentFileProposals
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {},
          this.props.t
        )
      });
    }
  }

  registerEventListener() {
    CamplaCoreService.addEventListener("UploadManagerDialog", event => {
      if (
        this.props.studentExamination &&
        event.model === "studentFileStorage" &&
        event.action === "update" &&
        this.state.studentFileProposalsIndexMap.has(event.object._id)
      ) {
        const idx = this.state.studentFileProposalsIndexMap.get(
          event.object._id
        );
        const studentFileProposal = Object.assign(
          {},
          this.state.studentFileProposals[idx]
        );
        studentFileProposal["isAccepted"] = event.object.isAccepted;
        const studentFileProposals = [...this.state.studentFileProposals]
        studentFileProposals[idx] = studentFileProposal;
        this.setState({
          studentFileProposals: studentFileProposals
        });
      }
    });
  }

  async onDelete(id) {
    try {
      await CamplaCoreService.deleteStudentFile(id);
      this.state.studentFileProposalsIndexMap.delete(id);
      const newStudentFileProposalList = this.state.studentFileProposals.filter(
        sf => id !== sf._id
      );
      this.setState({
        studentFileProposalsIndexMap: this.state.studentFileProposalsIndexMap,
        studentFileProposals: [...newStudentFileProposalList]
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "ExaminationAccess.UploadManagerDialog.APIError.PreconditionFailed",
            NOT_FOUND:
              "ExaminationAccess.UploadManagerDialog.APIError.NotFound",
            CONFLICT: "ExaminationAccess.UploadManagerDialog.APIError.Conflict"
          },
          this.props.t
        )
      });
    }
  }

  async onUpload(files) {
    if (!this.props.studentExamination.examination._id) return [];

    const newStudentFiles = [];
    for (const idx in files) {
      try {
        newStudentFiles.push(
          await CamplaCoreService.uploadStudentFile(
            this.props.studentExamination.examination._id,
            files[idx]
          )
        );
        files[idx]["status"] = 1;
      } catch (err) {
        files[idx]["status"] = 2;
        this.setState({
          dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {
              PRECONDITION_FAILED:
                "ExaminationAccess.UploadManagerDialog.APIError.PreconditionFailed",
              NOT_FOUND:
                "ExaminationAccess.UploadManagerDialog.APIError.NotFound",
              BAD_REQUEST:
                "ExaminationAccess.UploadManagerDialog.APIError.BadRequest",
              CONFLICT:
                "ExaminationAccess.UploadManagerDialog.APIError.Conflict"
            },
            this.props.t
          )
        });
      }
    }
    newStudentFiles.forEach((sf, idx) =>
      this.state.studentFileProposalsIndexMap.set(
        sf._id,
        this.state.studentFileProposals.length + idx
      )
    );
    this.setState({
      studentFileProposalsIndexMap: this.state.studentFileProposalsIndexMap,
      studentFileProposals: Object.assign(
        [],
        [...this.state.studentFileProposals, ...newStudentFiles]
      )
    });
    return files;
  }

  onHide() {
    this.props.onHide();
  }

  render() {
    const { t } = this.props;
    return (
      <BaseDialog
        show={this.props.show}
        onHide={this.onHide}
        title={t("ExaminationAccess.UploadManagerDialog.Title")}
        leftButtonText={t("DialogClose")}
        leftButtonVariant="black"
        leftButtonCallback={this.onHide}
        body={(<Tabs defaultActiveKey="fileUpload">
          <Tab
            eventKey="fileOverview"
            tabClassName="item-link"
            title={t(
              "ExaminationAccess.UploadManagerDialog.FileOverviewTabTitle"
            )}
          >
            <FileOverviewTab
              onDelete={this.onDelete}
              studentFileProposals={this.state.studentFileProposals}
            />
          </Tab>
          <Tab
            eventKey="fileUpload"
            tabClassName="item-link"
            title={t(
              "ExaminationAccess.UploadManagerDialog.FileUploadTabTitle"
            )}
          >
            <FileUploadTab
              studentExamination={this.props.studentExamination}
              studentFileProposals={this.state.studentFileProposals}
              onUpload={this.onUpload}
            />
          </Tab>
        </Tabs>)}
      />
    );
  }
}

export default withTranslation()(UploadManagerDialog);
